import { defineStore } from "pinia";
import api from "@/api";
import {
  PublicApiKey,
  PublicApiKeyStatus,
} from "@/interfaces/public-api-key.interface";

interface State {
  loading: boolean;
  key: PublicApiKey | null;
  revealedAccessToken: string | null;
}

const BASE = "/v2/organisation/public-api-keys";

export const usePublicApiKeyStore = defineStore("publicApiKey", {
  state: (): State => ({
    loading: false,
    key: null,
    revealedAccessToken: null,
  }),

  actions: {
    clear() {
      this.key = null;
      this.revealedAccessToken = null;
    },

    async fetchKey(schoolGroupId: string): Promise<void> {
      this.loading = true;
      this.revealedAccessToken = null;
      try {
        const response = await api.get(`${BASE}/${schoolGroupId}`);
        this.key = response.data as PublicApiKey;
      } catch (error: any) {
        if (error?.response?.status === 404) {
          this.key = null;
        } else {
          console.error("Error fetching public API key:", error);
          this.key = null;
          throw error;
        }
      } finally {
        this.loading = false;
      }
    },

    async createKey(schoolGroupId: string): Promise<PublicApiKey | null> {
      this.loading = true;
      try {
        const response = await api.post(BASE, { schoolGroupId });
        const created = response.data?.data as PublicApiKey;
        this.key = created || null;
        this.revealedAccessToken = null;
        return created || null;
      } catch (error) {
        console.error("Error creating public API key:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async revealKey(id: string): Promise<string | null> {
      try {
        const response = await api.get(`${BASE}/${id}/reveal`);
        const token = response.data?.accessToken as string | undefined;
        // Guard against a school-group/key switch mid-reveal: only write the
        // plaintext token if the still-selected key is the one we revealed.
        if (this.key && this.key._id !== id) {
          return token || null;
        }
        this.revealedAccessToken = token || null;
        return this.revealedAccessToken;
      } catch (error) {
        console.error("Error revealing public API key:", error);
        throw error;
      }
    },

    hideKey() {
      this.revealedAccessToken = null;
    },

    async regenerateKey(id: string): Promise<string | null> {
      this.loading = true;
      try {
        const response = await api.post(`${BASE}/regenerate/${id}`, {});
        const token = response.data?.accessToken as string | undefined;
        this.revealedAccessToken = token || null;
        return token || null;
      } catch (error) {
        console.error("Error regenerating public API key:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async setStatus(id: string, status: PublicApiKeyStatus): Promise<void> {
      this.loading = true;
      try {
        await api.patch(`${BASE}/${id}`, { status });
        if (this.key && this.key._id === id) {
          this.key.status = status;
        }
      } catch (error) {
        console.error("Error updating public API key status:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async deleteKey(id: string): Promise<void> {
      this.loading = true;
      try {
        await api.delete(`${BASE}/${id}`);
        this.key = null;
        this.revealedAccessToken = null;
      } catch (error) {
        console.error("Error deleting public API key:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
  },
});
